// Feature flags for controlling new functionality rollout
const FEATURE_FLAGS = {
  // Dynamic design generation features
  DYNAMIC_DESIGN_GENERATION: {
    enabled: true, // 🚀 ENABLED FOR TESTING
    description: 'AI-powered dynamic scene generation using CESDK and background library',
    requiresBeta: false // Available to all users for testing
  },
  CONTENT_TYPE_SELECTOR: {
    enabled: true, // Auto-enabled - UI enhancement
    description: 'Photo/Text/Video design type selector with AI recommendations',
    requiresBeta: false
  },
  AI_CONTENT_ANALYSIS: {
    enabled: true, // Auto-enabled - core functionality
    description: 'AI-powered content analysis for image characteristics and sport detection',
    requiresBeta: false
  },
  
  // User-specific feature testing
  BETA_USER_FEATURES: import.meta.env.MODE === 'development', // Enable in dev for testing
  
  // Development flags
  DEV_MODE: import.meta.env.MODE === 'development',
  
  // NEW: Background Library Integration
  BACKGROUND_LIBRARY_INTEGRATION: {
    enabled: true, // 🚀 ENABLED
    description: 'Integration with 600+ HD sport backgrounds for optimal selection',
    requiresBeta: false
  },
  
  // NEW: Event Tag Enhancement
  EVENT_TAG_ANALYSIS: {
    enabled: true, // 🚀 ENABLED
    description: 'Enhanced content analysis using event tags and metadata',
    requiresBeta: false
  },
  
  // NEW: Real-time Scene Composition
  REALTIME_SCENE_COMPOSITION: {
    enabled: true, // 🚀 ENABLED
    description: 'Real-time CESDK scene composition with background removal',
    requiresBeta: false
  },
  
  // Advanced Features (Beta)
  ADVANCED_AI_ANALYSIS: {
    enabled: false,
    description: 'Advanced AI features using external APIs (OpenAI Vision, etc.)',
    requiresBeta: true
  },
  
  MULTI_SPORT_DETECTION: {
    enabled: false,
    description: 'Advanced multi-sport detection and cross-sport templates',
    requiresBeta: true
  },
  
  CUSTOM_BACKGROUND_UPLOAD: {
    enabled: false,
    description: 'Allow users to upload and integrate custom backgrounds',
    requiresBeta: true
  },
  
  // NEW: Smart Template Categorization
  SMART_TEMPLATE_CATEGORIZATION: {
    enabled: true, // Auto-enabled - improved template organization
    description: 'Smart template ranking and categorization based on content analysis',
    requiresBeta: false
  },
};

// Get feature flag value with fallback
export const getFeatureFlag = (flagName, defaultValue = false) => {
  // In development, allow URL params to override flags for testing
  if (FEATURE_FLAGS.DEV_MODE) {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFlag = urlParams.get(`flag_${flagName.toLowerCase()}`);
    if (urlFlag !== null) {
      return urlFlag === 'true';
    }
  }
  
  return FEATURE_FLAGS[flagName] ?? defaultValue;
};

// Check if user should see beta features
export const isBetaUser = (userEmail) => {
  const betaUsers = [
    // Add your email and team emails for testing
    'luke@playmakerpics.com',
    'john@playmakerpics.com', // Replace with your actual email
    // Add more beta testers as needed
  ];
  
  return getFeatureFlag('BETA_USER_FEATURES') && betaUsers.includes(userEmail);
};

// Feature flag hook for React components
export const useFeatureFlag = (flagName, userEmail = null) => {
  const flag = FEATURE_FLAGS[flagName];
  
  if (!flag) {
    console.warn(`⚠️ Unknown feature flag: ${flagName}`);
    return false;
  }
  
  // Check URL override first
  const urlParams = new URLSearchParams(window.location.search);
  const urlOverride = urlParams.get(`flag_${flagName.toLowerCase()}`);
  if (urlOverride === 'true') {
    console.log(`🔧 Feature flag ${flagName} enabled via URL override`);
    return true;
  }
  if (urlOverride === 'false') {
    console.log(`🔧 Feature flag ${flagName} disabled via URL override`);
    return false;
  }
  
  // Check if feature requires beta access
  if (flag.requiresBeta) {
    const isBeta = userEmail ? isBetaUser(userEmail) : false;
    if (!isBeta) {
      return false;
    }
  }
  
  return flag.enabled;
};

// Get all enabled features for current user
export const getEnabledFeatures = (userEmail = null) => {
  const enabled = {};
  
  Object.entries(FEATURE_FLAGS).forEach(([flagName, flag]) => {
    if (useFeatureFlag(flagName, userEmail)) {
      enabled[flagName] = {
        description: flag.description,
        isBeta: flag.requiresBeta
      };
    }
  });
  
  return enabled;
};

// Development helper to check all feature flags
export const debugFeatureFlags = (userEmail = null) => {
  const debug = {};
  const isBeta = userEmail ? isBetaUser(userEmail) : false;
  
  Object.entries(FEATURE_FLAGS).forEach(([flagName, flag]) => {
    debug[flagName] = {
      enabled: useFeatureFlag(flagName, userEmail),
      requiresBeta: flag.requiresBeta,
      userIsBeta: isBeta,
      description: flag.description
    };
  });
  
  console.table(debug);
  return debug;
};

// URL helper to test features
export const getFeatureFlagTestUrls = () => {
  const baseUrl = window.location.origin + window.location.pathname;
  const testUrls = {};
  
  Object.keys(FEATURE_FLAGS).forEach(flagName => {
    const paramName = `flag_${flagName.toLowerCase()}`;
    testUrls[flagName] = `${baseUrl}?${paramName}=true`;
  });
  
  return testUrls;
};

// Export for development/debugging
if (process.env.NODE_ENV === 'development') {
  window.debugFeatureFlags = debugFeatureFlags;
  window.getFeatureFlagTestUrls = getFeatureFlagTestUrls;
  console.log('🔧 Feature flag debugging available: debugFeatureFlags(), getFeatureFlagTestUrls()');
}

export default FEATURE_FLAGS; 